/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/timeGroupApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Time Groups.
    Centralizes all API calls related to Time Groups.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  TimeGroup,
  TimeGroupPayload,
  ListTimeGroupsResponse,
  TimeGroupResponse,
} from '../types';

export const TIME_GROUPS_ENDPOINT = '/api/telephony/time-groups';

/**
 * Generic API request helper
 */
async function requestTimeGroupsApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List Time Groups with pagination and search
 */
export async function fetchTimeGroups(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListTimeGroupsResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${TIME_GROUPS_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch time groups');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as TimeGroup[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single Time Group by ID
 */
export async function fetchTimeGroup(id: string | number): Promise<TimeGroup> {
  const { data } = await requestTimeGroupsApi<TimeGroup>(
    `${TIME_GROUPS_ENDPOINT}/${id}`,
    'Failed to fetch time group',
  );
  return data;
}

/**
 * Create new Time Group
 */
export async function createTimeGroup(
  payload: TimeGroupPayload,
): Promise<TimeGroup> {
  const { data } = await requestTimeGroupsApi<TimeGroup>(
    TIME_GROUPS_ENDPOINT,
    'Failed to create time group',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update Time Group
 */
export async function updateTimeGroup(
  id: string | number,
  payload: TimeGroupPayload,
): Promise<TimeGroup> {
  const { data } = await requestTimeGroupsApi<TimeGroup>(
    `${TIME_GROUPS_ENDPOINT}/${id}`,
    'Failed to update time group',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Delete Time Group
 */
export async function deleteTimeGroup(id: string | number): Promise<void> {
  await requestTimeGroupsApi<null>(
    `${TIME_GROUPS_ENDPOINT}/${id}`,
    'Failed to delete time group',
    {
      method: 'DELETE',
    },
  );
}

